import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router";
import {
  AlertTriangle,
  Check,
  CircleSlash2,
  Clock3,
  ExternalLink,
  GitBranch,
  Image as ImageIcon,
  Link2,
  RefreshCw,
  ShieldCheck,
  Unlink2,
} from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  IdentityArtifactFetchError,
  useIdentityContactSheet,
  useIdentityInspector,
  useIdentityManifestHistory,
  useIdentityReport,
} from "./identityWorkbenchApi";
import type {
  IdentityManifestHistoryItem,
  IdentityReport,
  SignedIdentityArtifact,
} from "./identityWorkbenchContracts";
import {
  buildPairwiseMatrix,
  buildTimelineTracklets,
  formatPpm,
  identityHintWarnings,
  matrixCell,
  pseudonymousSubjectLabel,
  selectedManifest,
} from "./identityWorkbenchModels";
import type {
  IdentityDeepLinkHints,
  IdentityWorkbenchAction,
  IdentityWorkbenchPanel,
  IdentityWorkbenchState,
} from "./identityWorkbenchState";

export type IdentityWorkbenchDialogProps = {
  sessionId: string;
  state: IdentityWorkbenchState;
  dispatch: (action: IdentityWorkbenchAction) => void;
  hints: IdentityDeepLinkHints;
  onOpenFraming: (sceneId: string) => void;
};

const PANEL_LABELS: Record<IdentityWorkbenchPanel, string> = {
  gallery: "Gallery",
  comparison: "Comparison",
  matrix: "Pairwise matrix",
  timeline: "Track timeline",
  history: "Decision history",
  downstream: "Downstream use",
};

function artifactByKind(
  artifacts: readonly SignedIdentityArtifact[],
  kind: SignedIdentityArtifact["kind"],
) {
  return artifacts.filter((item) => item.kind === kind);
}

function reportPanelAvailability(
  report: IdentityReport | null,
  panel: keyof IdentityReport["panel_availability"],
) {
  return report?.panel_availability[panel] ?? "not_recorded";
}

function panelState(availability: string, hasData: boolean) {
  if (availability !== "available" && availability !== "partial") {
    return availability;
  }
  return hasData ? availability : "not_recorded";
}

function evidenceErrorState(error: unknown) {
  if (error instanceof IdentityArtifactFetchError) {
    if (error.code === "identity_artifact_expired") return "expired";
    if (error.code.includes("digest") || error.code.includes("correlation")) {
      return "mismatched";
    }
  }
  return "failed";
}

export function IdentityWorkbenchDialog({
  sessionId,
  state,
  dispatch,
  hints,
  onOpenFraming,
}: IdentityWorkbenchDialogProps) {
  const location = useLocation();
  const historyQuery = useIdentityManifestHistory(sessionId, state.open);
  const manifests = historyQuery.data?.data ?? [];
  const manifest = selectedManifest(manifests, state.manifestVersion);
  const identityId = manifest?.subjects.some(
    (subject) => subject.echo_identity_id === state.identityId,
  )
    ? state.identityId
    : (manifest?.subjects[0]?.echo_identity_id ?? null);
  const inspectorRequest =
    state.open &&
    manifest?.manifest_version != null &&
    manifest.identity_run_id &&
    identityId
      ? {
          sessionId,
          identityId,
          manifestVersion: manifest.manifest_version,
          identityRunId: manifest.identity_run_id,
        }
      : null;
  const inspectorQuery = useIdentityInspector(
    inspectorRequest,
    state.refreshEpoch,
  );
  const reports = artifactByKind(
    inspectorQuery.data?.artifacts ?? [],
    "identity_report",
  );
  const selectedReport =
    reports.find(
      (item) => item.artifact_id === state.selectedReportArtifactId,
    ) ??
    reports[0] ??
    null;
  const expectedReport =
    inspectorQuery.data?.manifest_digest && inspectorRequest
      ? {
          sessionId,
          manifestVersion: inspectorRequest.manifestVersion,
          manifestDigest: inspectorQuery.data.manifest_digest,
          identityRunId: inspectorRequest.identityRunId,
        }
      : null;
  const reportQuery = useIdentityReport(
    selectedReport,
    expectedReport,
    state.refreshEpoch,
  );
  const report = reportQuery.data ?? null;
  const sheets = artifactByKind(
    inspectorQuery.data?.artifacts ?? [],
    "identity_contact_sheet",
  );
  const selectedSheet = sheets[0] ?? null;
  const sheetQuery = useIdentityContactSheet(selectedSheet, state.refreshEpoch);
  const [sheetObjectUrl, setSheetObjectUrl] = useState<string | null>(null);
  const expiryRefreshAttempted = useRef(false);
  const selectionKey = `${manifest?.manifest_version ?? "none"}:${identityId ?? "none"}`;

  useEffect(() => {
    expiryRefreshAttempted.current = false;
  }, [selectionKey]);

  useEffect(() => {
    if (!sheetQuery.data) {
      setSheetObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(
      new Blob([sheetQuery.data], { type: "image/jpeg" }),
    );
    setSheetObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [sheetQuery.data]);

  useEffect(() => {
    const expired = [reportQuery.error, sheetQuery.error].some(
      (error) =>
        error instanceof IdentityArtifactFetchError &&
        error.code === "identity_artifact_expired",
    );
    if (expired && !expiryRefreshAttempted.current) {
      expiryRefreshAttempted.current = true;
      dispatch({ type: "evidence_refreshed" });
    }
  }, [dispatch, reportQuery.error, sheetQuery.error]);

  const warnings = useMemo(
    () => identityHintWarnings(manifest, hints),
    [hints, manifest],
  );
  const reportAvailability = reportQuery.isLoading
    ? "pending"
    : reportQuery.isError
      ? evidenceErrorState(reportQuery.error)
      : report
        ? "available"
        : (inspectorQuery.data?.artifact_availability ?? "not_recorded");

  return (
    <Dialog
      open={state.open}
      onOpenChange={(open) => {
        if (!open) dispatch({ type: "close" });
      }}
    >
      <DialogContent
        className="h-[96vh] w-[98vw] max-w-none overflow-y-auto sm:max-w-none"
        data-testid="identity-workbench"
      >
        <DialogTitle className="flex items-center gap-2">
          <ShieldCheck aria-hidden="true" className="size-5" />
          Identity evidence workbench
        </DialogTitle>
        <DialogDescription>
          Read-only, administrator-authorized evidence for one Echo-local pet
          identity. Signed media is verified in memory and never placed in the
          address bar or persisted query cache.
        </DialogDescription>

        {warnings.length > 0 ? (
          <Alert className="border-amber-400/70 bg-amber-50 dark:bg-amber-950/30">
            <AlertTriangle aria-hidden="true" />
            <AlertTitle>Deep-link hint was not authorized</AlertTitle>
            <AlertDescription>
              {warnings.map((warning) => (
                <p key={warning}>{warning}</p>
              ))}
            </AlertDescription>
          </Alert>
        ) : null}

        <WorkbenchControls
          manifests={manifests}
          manifest={manifest}
          identityId={identityId}
          reports={reports}
          selectedReport={selectedReport}
          dispatch={dispatch}
          onRefresh={() => dispatch({ type: "evidence_refreshed" })}
        />

        {historyQuery.isLoading ? (
          <EvidenceState state="pending" label="Loading manifest history" />
        ) : historyQuery.isError ? (
          <EvidenceState state="failed" label="Manifest history unavailable" />
        ) : manifest == null ? (
          <EvidenceState
            state="not_recorded"
            label="No identity manifest recorded"
          />
        ) : identityId == null ? (
          <EvidenceState
            state="not_recorded"
            label="This manifest has no displayable pet identities"
          />
        ) : inspectorQuery.isLoading ? (
          <EvidenceState state="pending" label="Loading authorized evidence" />
        ) : inspectorQuery.isError ? (
          <EvidenceState
            state="failed"
            label="Authorized identity evidence is unavailable"
          />
        ) : (
          <Tabs
            value={state.panel}
            onValueChange={(value) =>
              dispatch({
                type: "set_panel",
                panel: value as IdentityWorkbenchPanel,
              })
            }
          >
            <TabsList className="h-auto w-full justify-start overflow-x-auto">
              {Object.entries(PANEL_LABELS).map(([key, value]) => (
                <TabsTrigger key={key} value={key}>
                  {value}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="gallery">
              <GalleryPanel
                report={report}
                identityId={identityId}
                objectUrl={sheetObjectUrl}
                loading={sheetQuery.isLoading}
                error={sheetQuery.error}
                artifact={selectedSheet}
                artifactAvailability={
                  inspectorQuery.data?.artifact_availability ?? "not_recorded"
                }
              />
            </TabsContent>
            <TabsContent value="comparison">
              <ComparisonPanel
                report={report}
                identityId={identityId}
                selectedInstanceId={state.selectedInstanceId}
                onSelectInstance={(instanceId) =>
                  dispatch({ type: "select_instance", instanceId })
                }
                availability={
                  reportAvailability === "available"
                    ? reportPanelAvailability(report, "candidate_comparison")
                    : reportAvailability
                }
              />
            </TabsContent>
            <TabsContent value="matrix">
              <PairwisePanel
                report={report}
                availability={
                  reportAvailability === "available"
                    ? reportPanelAvailability(report, "pairwise_matrix")
                    : reportAvailability
                }
              />
            </TabsContent>
            <TabsContent value="timeline">
              <TimelinePanel
                report={report}
                identityId={identityId}
                availability={
                  reportAvailability === "available"
                    ? reportPanelAvailability(report, "track_timeline")
                    : reportAvailability
                }
                timeUs={state.timeUs}
                onTimeChange={(timeUs) =>
                  dispatch({ type: "set_time", timeUs })
                }
              />
            </TabsContent>
            <TabsContent value="history">
              <DecisionHistoryPanel
                report={report}
                availability={
                  reportAvailability === "available"
                    ? reportPanelAvailability(report, "decision_history")
                    : reportAvailability
                }
              />
            </TabsContent>
            <TabsContent value="downstream">
              <DownstreamPanel
                manifest={manifest}
                report={report}
                pathname={location.pathname}
                onOpenFraming={onOpenFraming}
              />
            </TabsContent>
          </Tabs>
        )}

        <footer className="flex flex-wrap items-center gap-2 border-t pt-3 text-xs text-muted-foreground">
          <Badge variant="outline">
            manifest {manifest?.manifest_digest?.slice(7, 19) ?? "not recorded"}
          </Badge>
          <Badge variant="outline">
            config {manifest?.config_release_id ?? "not recorded"}
          </Badge>
          <Badge variant="outline" className="capitalize">
            {manifest?.mode === "unknown"
              ? "mode not recorded"
              : `${manifest?.mode ?? "unknown"} mode`}
          </Badge>
          <Badge variant="outline">
            {manifest?.inference_executed === true
              ? "real ML inference"
              : manifest?.inference_executed === false
                ? "inference not executed"
                : "inference provenance not recorded"}
          </Badge>
          {historyQuery.data?.truncated ? (
            <Badge variant="secondary">version history truncated</Badge>
          ) : null}
          <span>
            Human matching is denied. Storage locations and feature artifacts
            never enter this client contract.
          </span>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

function WorkbenchControls({
  manifests,
  manifest,
  identityId,
  reports,
  selectedReport,
  dispatch,
  onRefresh,
}: {
  manifests: IdentityManifestHistoryItem[];
  manifest: IdentityManifestHistoryItem | null;
  identityId: string | null;
  reports: SignedIdentityArtifact[];
  selectedReport: SignedIdentityArtifact | null;
  dispatch: (action: IdentityWorkbenchAction) => void;
  onRefresh: () => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border p-3">
      <label className="grid gap-1 text-xs text-muted-foreground">
        Immutable manifest version
        <Select
          value={manifest?.manifest_version?.toString() ?? ""}
          onValueChange={(value) =>
            dispatch({
              type: "select_manifest",
              manifestVersion: Number(value),
            })
          }
        >
          <SelectTrigger className="min-w-52" aria-label="Manifest version">
            <SelectValue placeholder="No manifest" />
          </SelectTrigger>
          <SelectContent>
            {manifests.map((item) => (
              <SelectItem
                key={`${item.manifest_version}:${item.manifest_digest}`}
                value={String(item.manifest_version)}
              >
                v{item.manifest_version} ·{" "}
                {item.manifest_status.replaceAll("_", " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      <label className="grid gap-1 text-xs text-muted-foreground">
        Pet identity
        <Select
          value={identityId ?? ""}
          onValueChange={(value) =>
            dispatch({ type: "select_identity", identityId: value })
          }
        >
          <SelectTrigger className="min-w-48" aria-label="Pet identity">
            <SelectValue placeholder="No identity" />
          </SelectTrigger>
          <SelectContent>
            {manifest?.subjects.map((subject) => (
              <SelectItem
                key={subject.echo_identity_id}
                value={subject.echo_identity_id}
              >
                {pseudonymousSubjectLabel(
                  manifest.subjects,
                  subject.echo_identity_id,
                )}{" "}
                · {subject.resolution_status.replaceAll("_", " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      {reports.length > 1 ? (
        <label className="grid gap-1 text-xs text-muted-foreground">
          Evidence write
          <Select
            value={selectedReport?.artifact_id ?? ""}
            onValueChange={(value) =>
              dispatch({ type: "select_report", artifactId: value })
            }
          >
            <SelectTrigger className="min-w-48" aria-label="Evidence write">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {reports.map((item, index) => (
                <SelectItem key={item.artifact_id} value={item.artifact_id}>
                  Report {index + 1} · {item.content_digest.slice(7, 15)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      ) : null}
      <Button type="button" variant="outline" size="sm" onClick={onRefresh}>
        <RefreshCw aria-hidden="true" className="size-3.5" />
        Refresh signed evidence
      </Button>
    </div>
  );
}

function PanelShell({
  title,
  icon,
  availability,
  children,
}: {
  title: string;
  icon: ReactNode;
  availability: string;
  children?: ReactNode;
}) {
  const usable = availability === "available" || availability === "partial";
  return (
    <section className="rounded-lg border p-4" aria-label={title}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-medium">
          {icon}
          {title}
        </h3>
        <AvailabilityBadge value={availability} />
      </div>
      {usable ? (
        <div className="mt-4">{children}</div>
      ) : (
        <EvidenceState
          state={availability}
          label={`${title} is ${availability.replaceAll("_", " ")}`}
        />
      )}
    </section>
  );
}

function GalleryPanel({
  report,
  identityId,
  objectUrl,
  loading,
  error,
  artifact,
  artifactAvailability,
}: {
  report: IdentityReport | null;
  identityId: string;
  objectUrl: string | null;
  loading: boolean;
  error: unknown;
  artifact: SignedIdentityArtifact | null;
  artifactAvailability: string;
}) {
  const availability = loading
    ? "pending"
    : error
      ? evidenceErrorState(error)
      : objectUrl
        ? "available"
        : artifact
          ? artifactAvailability
          : ["deleted", "unavailable", "partial"].includes(artifactAvailability)
            ? artifactAvailability
            : "not_recorded";
  const subject = report?.subjects.find(
    (item) => item.echo_identity_id === identityId,
  );
  return (
    <PanelShell
      title="Identity gallery"
      icon={<ImageIcon aria-hidden="true" className="size-4" />}
      availability={availability}
    >
      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(16rem,1fr)]">
        {objectUrl ? (
          <img
            src={objectUrl}
            alt="Pseudonymous pet identity contact sheet"
            className="max-h-[65vh] w-full rounded-md border object-contain"
          />
        ) : null}
        <div>
          <p className="font-medium">
            {report
              ? pseudonymousSubjectLabel(report.subjects, identityId)
              : "Selected subject"}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {subject?.reference_count ?? 0} references ·{" "}
            {subject?.distinct_media_count ?? 0} source assets ·{" "}
            {subject?.distinct_tracklet_count ?? 0} tracklets
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {subject?.views.map((view) => (
              <Badge key={view} variant="outline">
                {view.replaceAll("_", " ")}
              </Badge>
            ))}
          </div>
          {subject?.breed_observation ? (
            <div className="mt-4 rounded-md border bg-muted/30 p-3 text-sm">
              <p className="font-medium">
                {subject.breed_observation.status === "observed" &&
                subject.breed_observation.breed_key
                  ? `Likely visual breed: ${subject.breed_observation.breed_key
                      .replaceAll("_", " ")
                      .replaceAll("-", " ")
                      .replace(/\b\w/g, (letter) => letter.toUpperCase())}`
                  : subject.breed_observation.status === "abstained"
                    ? "Visual breed not determined"
                    : "Visual breed unavailable for this Echo"}
              </p>
              {subject.breed_observation.top_candidates.length > 0 ? (
                <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                  {subject.breed_observation.top_candidates.map((candidate) => (
                    <div
                      key={candidate.breed_key}
                      className="flex justify-between gap-4"
                    >
                      <span>{candidate.breed_key.replaceAll("_", " ")}</span>
                      <span>
                        {(candidate.probability_ppm / 10_000).toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
              <p className="mt-2 text-xs text-muted-foreground">
                Visual resemblance only, from{" "}
                {subject.breed_observation.sampled_crop_count} selected dog
                crops; not pedigree evidence.
              </p>
            </div>
          ) : (
            <p className="mt-4 text-xs text-muted-foreground">
              Breed not evaluated.
            </p>
          )}
          <p className="mt-4 text-xs text-muted-foreground">
            Digest verified before display:{" "}
            {artifact?.content_digest.slice(7, 19)}. Labels are derived
            ordinals; user-provided names are absent.
          </p>
        </div>
      </div>
    </PanelShell>
  );
}

function ComparisonPanel({
  report,
  identityId,
  selectedInstanceId,
  onSelectInstance,
  availability,
}: {
  report: IdentityReport | null;
  identityId: string;
  selectedInstanceId: string | null;
  onSelectInstance: (instanceId: string) => void;
  availability: string;
}) {
  const subjectInstances =
    report?.instances.filter(
      (item) => item.assigned_echo_identity_id === identityId,
    ) ?? [];
  const selectedInstance =
    subjectInstances.find((item) => item.instance_id === selectedInstanceId) ??
    subjectInstances[0] ??
    null;
  const assignments =
    report?.assignments.filter(
      (item) =>
        item.echo_identity_id === identityId &&
        (selectedInstance == null ||
          item.instance_ids.includes(selectedInstance.instance_id)),
    ) ?? [];
  return (
    <PanelShell
      title="Candidate comparison"
      icon={<GitBranch aria-hidden="true" className="size-4" />}
      availability={panelState(availability, assignments.length > 0)}
    >
      {subjectInstances.length > 0 ? (
        <div className="mb-4">
          <p className="text-xs font-medium">Selected source observation</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {subjectInstances.map((item, index) => (
              <Button
                type="button"
                key={item.instance_id}
                size="sm"
                variant={
                  item.instance_id === selectedInstance?.instance_id
                    ? "default"
                    : "outline"
                }
                onClick={() => onSelectInstance(item.instance_id)}
              >
                Observation {index + 1} · {item.quality.view}
              </Button>
            ))}
          </div>
          {selectedInstance ? (
            <p className="mt-2 text-xs text-muted-foreground">
              Normalized box {selectedInstance.box.x_ppm},{" "}
              {selectedInstance.box.y_ppm}, {selectedInstance.box.width_ppm} ×{" "}
              {selectedInstance.box.height_ppm} ppm · detector{" "}
              {formatPpm(selectedInstance.detector_confidence_ppm)}
            </p>
          ) : null}
        </div>
      ) : null}
      <p className="mb-3 text-xs text-muted-foreground">
        Pet-only calibrated scores are shown. The frozen acceptance/ask
        thresholds are not reconstructed when they were not recorded in this
        artifact.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <caption className="sr-only">
            Candidate assignment scores and reasons
          </caption>
          <thead>
            <tr className="border-b text-left">
              <th className="p-2">Assignment</th>
              <th className="p-2">Status</th>
              <th className="p-2">Top score</th>
              <th className="p-2">Margin</th>
              <th className="p-2">Support</th>
              <th className="p-2">Reason</th>
            </tr>
          </thead>
          <tbody>
            {assignments.map((item, index) => (
              <tr key={item.assignment_id} className="border-b last:border-0">
                <td className="p-2">Candidate {index + 1}</td>
                <td className="p-2 capitalize">
                  {item.status.replaceAll("_", " ")}
                </td>
                <td className="p-2 font-mono">
                  {formatPpm(item.top_candidate_ppm)}
                </td>
                <td className="p-2 font-mono">{formatPpm(item.margin_ppm)}</td>
                <td className="p-2">{item.support_evidence_count}</td>
                <td className="p-2">
                  {item.reasons.join(", ").replaceAll("_", " ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PanelShell>
  );
}

function PairwisePanel({
  report,
  availability,
}: {
  report: IdentityReport | null;
  availability: string;
}) {
  const matrix = report ? buildPairwiseMatrix(report) : null;
  return (
    <PanelShell
      title="Sparse pairwise constraint matrix"
      icon={<Unlink2 aria-hidden="true" className="size-4" />}
      availability={panelState(
        availability,
        Boolean(matrix && matrix.subjectIds.length > 0),
      )}
    >
      {matrix && report ? (
        <>
          <p className="mb-3 text-xs text-muted-foreground">
            Categorical hard constraints are available. Exact pet pair scores
            and frozen decision thresholds remain explicitly not recorded when
            absent from identity-report.v1.
          </p>
          <div className="mb-3 flex flex-wrap gap-2 text-xs">
            <Badge variant="outline">
              <Unlink2 className="size-3" /> Cannot link
            </Badge>
            <Badge variant="outline">
              <Link2 className="size-3" /> Must link
            </Badge>
            <Badge variant="outline">
              <CircleSlash2 className="size-3" /> Not recorded
            </Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="border-collapse text-xs">
              <caption className="sr-only">
                Pairwise identity relationship matrix with explicit cannot-link
                and must-link labels
              </caption>
              <thead>
                <tr>
                  <th className="border p-2">Identity</th>
                  {matrix.subjectIds.map((id) => (
                    <th key={id} className="border p-2">
                      {pseudonymousSubjectLabel(report.subjects, id)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.subjectIds.map((left) => (
                  <tr key={left}>
                    <th className="border p-2 text-left">
                      {pseudonymousSubjectLabel(report.subjects, left)}
                    </th>
                    {matrix.subjectIds.map((right) => {
                      const cell = matrixCell(matrix, left, right);
                      return (
                        <td
                          key={right}
                          className={`border p-2 text-center ${
                            cell.relation === "cannot_link"
                              ? "bg-[repeating-linear-gradient(135deg,transparent,transparent_5px,rgba(239,68,68,.16)_5px,rgba(239,68,68,.16)_10px)]"
                              : cell.relation === "must_link"
                                ? "bg-emerald-50 dark:bg-emerald-950/30"
                                : ""
                          }`}
                          title={cell.reasons.join(", ") || cell.relation}
                        >
                          {cell.relation.replaceAll("_", " ")}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {matrix.truncated || report.truncation.decisions ? (
            <p className="mt-3 text-xs text-muted-foreground">
              Matrix is explicitly truncated to the safe browser bound.
            </p>
          ) : null}
        </>
      ) : null}
    </PanelShell>
  );
}

function TimelinePanel({
  report,
  identityId,
  availability,
  timeUs,
  onTimeChange,
}: {
  report: IdentityReport | null;
  identityId: string;
  availability: string;
  timeUs: number;
  onTimeChange: (timeUs: number) => void;
}) {
  const timeline = report ? buildTimelineTracklets(report, identityId) : null;
  return (
    <PanelShell
      title="Video track timeline"
      icon={<Clock3 aria-hidden="true" className="size-4" />}
      availability={panelState(
        availability,
        Boolean(timeline && timeline.tracklets.length > 0),
      )}
    >
      {timeline ? (
        <div className="space-y-3">
          {timeline.tracklets.map((item) => (
            <button
              type="button"
              key={item.tracklet_id}
              className="grid w-full grid-cols-[minmax(8rem,12rem)_1fr] items-center gap-3 text-left text-xs"
              onClick={() => onTimeChange(item.start_us)}
              aria-label={`Tracklet from ${(item.start_us / 1_000_000).toFixed(2)} to ${(item.end_us / 1_000_000).toFixed(2)} seconds`}
            >
              <span className="truncate">
                {item.source_media_id} · {item.quality}
              </span>
              <span className="relative h-7 rounded bg-muted">
                <span
                  className="absolute top-1 h-5 rounded border border-blue-600 bg-blue-500/30"
                  style={{
                    left: `${item.leftPercent}%`,
                    width: `${Math.min(100 - item.leftPercent, item.widthPercent)}%`,
                  }}
                />
              </span>
            </button>
          ))}
          <p className="text-xs text-muted-foreground">
            Selected time {(timeUs / 1_000_000).toFixed(2)}s. Gaps between bars
            are explicit occlusion/re-entry gaps; no continuity is inferred
            across them.
          </p>
          {timeline.truncated || report?.truncation.tracklets ? (
            <Badge variant="secondary">timeline truncated</Badge>
          ) : null}
          <table className="sr-only">
            <caption>Track spans for screen readers</caption>
            <thead>
              <tr>
                <th>Media</th>
                <th>Start</th>
                <th>End</th>
                <th>Views</th>
              </tr>
            </thead>
            <tbody>
              {timeline.tracklets.map((item) => (
                <tr key={item.tracklet_id}>
                  <td>{item.source_media_id}</td>
                  <td>{item.start_us}</td>
                  <td>{item.end_us}</td>
                  <td>{item.view_coverage.join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </PanelShell>
  );
}

function DecisionHistoryPanel({
  report,
  availability,
}: {
  report: IdentityReport | null;
  availability: string;
}) {
  return (
    <PanelShell
      title="Immutable decision history"
      icon={<GitBranch aria-hidden="true" className="size-4" />}
      availability={panelState(availability, Boolean(report?.decisions.length))}
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <caption className="sr-only">Identity decision history</caption>
          <thead>
            <tr className="border-b text-left">
              <th className="p-2">Time</th>
              <th className="p-2">Actor</th>
              <th className="p-2">Decision</th>
              <th className="p-2">Reason</th>
              <th className="p-2">Result</th>
            </tr>
          </thead>
          <tbody>
            {report?.decisions.map((item) => (
              <tr key={item.decision_id} className="border-b last:border-0">
                <td className="p-2">{item.created_at}</td>
                <td className="p-2 capitalize">
                  {item.actor.replaceAll("_", " ")}
                </td>
                <td className="p-2 capitalize">
                  {item.decision_type.replaceAll("_", " ")}
                </td>
                <td className="p-2">
                  {item.reasons.join(", ").replaceAll("_", " ")}
                </td>
                <td className="p-2 capitalize">
                  {item.downstream_result.replaceAll("_", " ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {report?.truncation.decisions ? (
        <Badge className="mt-3" variant="secondary">
          decision history truncated
        </Badge>
      ) : null}
    </PanelShell>
  );
}

function DownstreamPanel({
  manifest,
  report,
  pathname,
  onOpenFraming,
}: {
  manifest: IdentityManifestHistoryItem;
  report: IdentityReport | null;
  pathname: string;
  onOpenFraming: (sceneId: string) => void;
}) {
  const available =
    manifest.scene_ids.length > 0 ||
    Object.keys(manifest.downstream_counts).length > 0 ||
    Object.keys(manifest.qc_counts).length > 0;
  return (
    <PanelShell
      title="Downstream scene and QC use"
      icon={<ExternalLink aria-hidden="true" className="size-4" />}
      availability={available ? "available" : "not_recorded"}
    >
      <div className="grid gap-4 md:grid-cols-2">
        <dl className="grid grid-cols-2 gap-2 text-sm">
          {Object.entries(manifest.downstream_counts).map(([key, value]) => (
            <div key={key} className="rounded border p-2">
              <dt className="text-xs text-muted-foreground">
                {key.replaceAll("_", " ")}
              </dt>
              <dd className="font-semibold">{value}</dd>
            </div>
          ))}
          {Object.entries(manifest.qc_counts).map(([key, value]) => (
            <div key={key} className="rounded border p-2">
              <dt className="text-xs text-muted-foreground">
                QC {key.replaceAll("_", " ")}
              </dt>
              <dd className="font-semibold">{value}</dd>
            </div>
          ))}
        </dl>
        <div>
          <p className="text-sm font-medium">Affected framing scenes</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {manifest.scene_ids.map((sceneId) => {
              const query = new URLSearchParams({
                identityEvidence: "1",
                framingScene: sceneId,
              });
              return (
                <span key={sceneId} className="inline-flex items-center gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onOpenFraming(sceneId)}
                  >
                    Scene {sceneId}
                  </Button>
                  <a
                    href={`${pathname}?${query.toString()}`}
                    className="rounded p-1 text-muted-foreground hover:text-foreground"
                    aria-label={`Copyable deep link for framing scene ${sceneId}`}
                  >
                    <ExternalLink aria-hidden="true" className="size-3.5" />
                  </a>
                </span>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Deep links contain only authorized selection hints. Attempt, stage,
            and timestamp are omitted when this identity report did not record
            them.
          </p>
        </div>
      </div>
      {report ? (
        <p className="mt-4 text-xs text-muted-foreground">
          Director/composer receipts: manifest{" "}
          {report.manifest_digest.slice(7, 19)}, config{" "}
          {report.config_release_id}, models{" "}
          {Object.values(report.model_release_ids).join(", ")}.
        </p>
      ) : null}
    </PanelShell>
  );
}

function AvailabilityBadge({ value }: { value: string }) {
  const icon =
    value === "available" ? (
      <Check aria-hidden="true" className="size-3" />
    ) : value === "pending" ? (
      <Clock3 aria-hidden="true" className="size-3" />
    ) : (
      <CircleSlash2 aria-hidden="true" className="size-3" />
    );
  return (
    <Badge
      variant={
        value === "failed" || value === "mismatched" ? "destructive" : "outline"
      }
    >
      {icon}
      {value.replaceAll("_", " ")}
    </Badge>
  );
}

function EvidenceState({ state, label }: { state: string; label: string }) {
  return (
    <div
      role="status"
      className="mt-4 rounded-md border border-dashed p-4 text-sm text-muted-foreground"
    >
      <AvailabilityBadge value={state} />
      <p className="mt-2">{label}</p>
    </div>
  );
}
